import posthog from 'posthog-js';

import { FORCED_FEATURE_FLAGS, POSTHOG_HOST, POSTHOG_KEY } from '@/lib/env';
import { getAnalyticsConsent, onAnalyticsConsent } from '@/lib/consent';
import { isCaptureAllowed } from './capture-policy';
import { buildTelemetryConfig } from './config';

/**
 * DEV-only local flag override, so a flag can be exercised without a PostHog project.
 * First match wins: localStorage `ff:<key>` ('1'/'true' | '0'/'false'), then the
 * `VITE_FEATURE_FLAGS` list (forced ON). Returns null when nothing overrides — normal
 * resolution then applies. Inert in production builds (`import.meta.env.DEV` is false),
 * and pure (no side effects), so `isFeatureEnabled` stays safe to call during render.
 */
function devFlagOverride(key: string): boolean | null {
  if (!import.meta.env.DEV) return null;
  try {
    const stored = typeof localStorage !== 'undefined' ? localStorage.getItem(`ff:${key}`) : null;
    if (stored === '1' || stored === 'true') return true;
    if (stored === '0' || stored === 'false') return false;
  } catch {
    /* storage blocked — fall through to the env list */
  }
  return FORCED_FEATURE_FLAGS.includes(key) ? true : null;
}

/**
 * The sole vendor adapter: `posthog-js` is imported here and nowhere else, so
 * swapping vendors touches only this file. Every export is inert when telemetry
 * is unconfigured or init throws — the app renders normally and flags read
 * default-off whether or not PostHog is reachable.
 *
 * Resilience contract: the SDK may be unreachable on the *first* call (blocked by
 * an extension, offline, CSP) and recover later. Rather than hardening each entry
 * point on its own — which leaves a long tail of "this path doesn't recover" — all
 * active entry points funnel through a single `reconcile()` that (1) ensures the
 * client is started, (2) flushes the desired identity, and (3) registers any flag
 * subscribers that could not register yet. So whichever entry point first finds
 * telemetry available brings identity AND subscriptions fully in sync; no path can
 * be left permanently stuck. Reads (`isFeatureEnabled`) stay pure and never
 * reconcile, so they remain safe to call during render.
 */

let started = false;
let warnedUnavailable = false;
// The identity we want PostHog to carry vs the one it currently carries. Tracking
// the desired id separately lets a later successful reconcile apply an identity
// that could not be set when auth first resolved (or whose `identify` threw).
let desiredDistinctId: string | null = null;
let identifiedDistinctId: string | null = null;

// Flag subscribers, each holding the vendor unsubscribe once registered (null
// while pending). A subscriber added before the client starts — or while a
// transient failure prevents registration — is flushed by the next reconcile.
type FlagSubscriber = { callback: () => void; vendorUnsubscribe: (() => void) | null };
const flagSubscribers = new Set<FlagSubscriber>();

export function isTelemetryStarted(): boolean {
  return started;
}

/**
 * Public start surface (provider mount effect, tests). Delegates to the single
 * `reconcile()` so starting also flushes any identity/subscriptions that auth or
 * a child effect resolved before the client was up.
 */
export function initTelemetry(): void {
  reconcile();
}

// The single reconciliation path. Every active entry point calls this so that
// whichever one first finds telemetry available syncs EVERYTHING — start, then
// identity, then pending subscriptions. Idempotent and cheap when already in sync.
function reconcile(): void {
  ensureStarted();
  if (!started || consentDenied) return;
  syncIdentity();
  flushFlagSubscribers();
}

// Idempotently starts the vendor client. Inert without a key or if init throws;
// a failed start warns once (not on every retry) while still allowing a later
// reconcile to start successfully so transient failures recover.
function ensureStarted(): void {
  if (started || !POSTHOG_KEY) return;
  // Prior blocking (VER-381): on production the SDK does not even initialize
  // until the cookie banner grants analytics. Off production the consent
  // capability short-circuits to granted, so staging/dev/e2e are unchanged.
  if (getAnalyticsConsent() !== 'granted') return;
  try {
    posthog.init(
      POSTHOG_KEY,
      buildTelemetryConfig(POSTHOG_HOST, isCaptureAllowed()) as Parameters<typeof posthog.init>[1],
    );
    started = true;
  } catch (error) {
    if (!warnedUnavailable) {
      console.warn('Telemetry unavailable; continuing without analytics.', error);
      warnedUnavailable = true;
    }
  }
}

// Brings PostHog's identity in line with the desired id. No-op until started and
// when already in sync. If `identify`/`reset` throws, the desired id stays unequal
// to the identified one, so the next reconcile retries — never permanently out of
// sync.
function syncIdentity(): void {
  if (!started || !desiredDistinctId || desiredDistinctId === identifiedDistinctId) return;
  try {
    // Switching straight from one identified user to another (an account switch
    // with no sign-out in between) must reset first, so the previous user's stored
    // properties/flags never bleed into the new identity. We do NOT reset on the
    // first identify (identified === null): that anon→known transition is meant to
    // link the pre-sign-in activity to the user. `identify` reloads feature flags
    // for the new distinct id, so subscribed hooks re-resolve.
    if (identifiedDistinctId !== null) {
      posthog.reset();
    }
    posthog.identify(desiredDistinctId);
    identifiedDistinctId = desiredDistinctId;
  } catch {
    // Leave out of sync; a later reconcile retries.
  }
}

// Registers flag subscribers that have no live vendor subscription yet. A throw
// leaves that subscriber pending so the next reconcile retries; already-registered
// subscribers are skipped, so this is idempotent across reconciles.
function flushFlagSubscribers(): void {
  // Snapshot: registering can synchronously fire a flag callback that adds or
  // removes a subscriber, mutating the live set mid-iteration.
  for (const subscriber of [...flagSubscribers]) {
    // Skip if it already has a live subscription, or was unsubscribed during this
    // flush (no longer in the set) — never re-register an orphan.
    if (subscriber.vendorUnsubscribe || !flagSubscribers.has(subscriber)) continue;
    try {
      const unsubscribe = posthog.onFeatureFlags(() => subscriber.callback());
      subscriber.vendorUnsubscribe = typeof unsubscribe === 'function' ? unsubscribe : () => {};
    } catch {
      // Leave pending; a later reconcile retries.
    }
  }
}

// While denied, the client behaves as if it never started: no identify (the
// revocation reset dropped the vendor identity and a reconcile must not
// re-link it), and flags read default-off — the stated decliner contract.
let consentDenied = false;

// Revocation (LGPD art. 8 §5): stop capturing, stop the replay, and drop the
// stored identity — the next grant starts clean. Each cleanup is its own
// best-effort: one SDK failure must not abort the remaining privacy cleanup.
function revokeTelemetry(): void {
  if (!started) return;
  try {
    posthog.stopSessionRecording();
  } catch {
    /* inert */
  }
  // reset() BEFORE opt_out: in this posthog-js version reset() also resets the
  // SDK's consent state — run it earlier and the revocation would end with the
  // SDK back in its default capturing state. The opt-out must be the final
  // consent operation so it sticks.
  try {
    posthog.reset();
  } catch {
    /* inert */
  }
  // The SDK-owned handlers (autocapture, history-change pageviews, pageleave,
  // dead clicks, exception capture) were configured ON at init and outlive a
  // failed opt-out — our wrapper guards never see their events, so the config
  // itself must flip off.
  try {
    posthog.set_config(buildTelemetryConfig(POSTHOG_HOST, false) as Parameters<typeof posthog.set_config>[0]);
  } catch {
    /* inert */
  }
  try {
    posthog.opt_out_capturing();
  } catch {
    /* inert */
  }
  // The vendor identity is gone — without this, a later re-grant would see
  // desired === identified and skip identify, leaving the session anonymous.
  identifiedDistinctId = null;
}

// A consent change is an entry point like any other: a grant starts (or
// re-opts) the client and flushes identity + flag subscribers; a denial
// revokes. Registered at module scope so no surface has to remember to wire it.
onAnalyticsConsent((status) => {
  if (status === 'granted') {
    consentDenied = false;
    if (started) {
      try {
        // Mirror of the revocation's config flip — the SDK-owned handlers
        // come back before the opt-in.
        posthog.set_config(buildTelemetryConfig(POSTHOG_HOST, isCaptureAllowed()) as Parameters<typeof posthog.set_config>[0]);
      } catch {
        /* inert */
      }
      try {
        posthog.opt_in_capturing();
      } catch {
        /* inert */
      }
    }
    reconcile();
    notifyFlagSubscribers(); // flags come back from default-off
    return;
  }
  consentDenied = true;
  revokeTelemetry();
  notifyFlagSubscribers(); // every flag now reads default-off
});

function notifyFlagSubscribers(): void {
  for (const subscriber of [...flagSubscribers]) {
    try {
      subscriber.callback();
    } catch {
      /* one bad subscriber must not starve the rest */
    }
  }
}

function resetIdentity(): void {
  if (!started) return;
  try {
    posthog.reset();
  } catch {
    /* inert */
  }
}

/**
 * Reconciles telemetry identity with auth state. The distinct id is ALWAYS the
 * pseudonymous Clerk user id — never a name, email, or CPF.
 */
export function applyIdentity(auth: {
  isSignedIn: boolean | undefined;
  userId: string | null | undefined;
}): void {
  if (auth.isSignedIn && auth.userId) {
    desiredDistinctId = auth.userId;
    reconcile(); // self-start + flush the identity on the first available path
  } else if (auth.isSignedIn === false) {
    desiredDistinctId = null;
    // Reset ONLY on a real sign-out transition (was-identified → signed-out), to
    // clear the previous user. Never on plain anonymous loads — resetting there
    // mints a fresh anonymous id every load and inflates unique-user counts.
    if (identifiedDistinctId !== null) {
      resetIdentity();
      identifiedDistinctId = null;
    }
  }
}

export function captureEvent(name: string, properties?: Record<string, unknown>): void {
  if (consentDenied) return; // fail closed — the vendor opt-out call is best-effort only
  if (!isCaptureAllowed()) return; // prod-only data (see capture-policy)
  reconcile();
  if (!started) return;
  try {
    posthog.capture(name, properties);
  } catch {
    /* inert */
  }
}

export function captureError(error: unknown, properties?: Record<string, unknown>): void {
  if (consentDenied) return; // fail closed — the vendor opt-out call is best-effort only
  if (!isCaptureAllowed()) return; // prod-only data (see capture-policy)
  reconcile();
  if (!started) return;
  try {
    posthog.captureException(error, properties);
  } catch {
    /* inert */
  }
}

// Person-property values must already be PII-free (counts/booleans/enums) —
// enforced at the caller, not here.
export function capturePersonProperties(properties: Record<string, unknown>): void {
  if (consentDenied) return; // fail closed — the vendor opt-out call is best-effort only
  if (!isCaptureAllowed()) return; // prod-only data (see capture-policy)
  reconcile();
  if (!started) return;
  try {
    posthog.setPersonProperties(properties);
  } catch {
    /* inert */
  }
}

// Pure read (no reconcile/self-start): a flag query must not boot the SDK, so it
// is safe in a render-phase initializer. The subscription path starts the client.
export function isFeatureEnabled(key: string): boolean {
  const override = devFlagOverride(key);
  if (override !== null) return override; // DEV-only local override (no-op in prod)
  if (!started || consentDenied) return false; // default-off when unavailable/unresolved/denied
  try {
    return posthog.isFeatureEnabled(key) === true;
  } catch {
    return false;
  }
}

/**
 * The variant a multivariate flag resolves to for this person, or null while it is
 * unresolved, when telemetry is unavailable or consent is denied, or for a boolean
 * flag. Pure, like `isFeatureEnabled`. DEV-only: localStorage `ff:<key>` holding a
 * variant name (anything but the boolean words) forces that variant.
 */
export function getFeatureFlagVariant(key: string): string | null {
  const override = devFlagVariantOverride(key);
  if (override !== null) return override;
  if (!started || consentDenied) return null;
  try {
    const value = posthog.getFeatureFlag(key);
    return typeof value === 'string' ? value : null;
  } catch {
    return null;
  }
}

function devFlagVariantOverride(key: string): string | null {
  if (!import.meta.env.DEV) return null;
  try {
    const stored = typeof localStorage !== 'undefined' ? localStorage.getItem(`ff:${key}`) : null;
    if (!stored || ['1', '0', 'true', 'false'].includes(stored)) return null;
    return stored;
  } catch {
    return null;
  }
}

/**
 * Subscribes to flag changes. The subscriber is tracked so that if telemetry is
 * not started yet (or registration throws), a later reconcile from any entry point
 * registers it — an existing `useFeatureFlags` hook can never stay stuck
 * default-off once telemetry becomes available. Returns an unsubscribe valid
 * whether or not the vendor subscription was ever established.
 */
export function onFeatureFlags(callback: () => void): () => void {
  const subscriber: FlagSubscriber = { callback, vendorUnsubscribe: null };
  flagSubscribers.add(subscriber);
  reconcile(); // start + register this (and any other pending) subscriber if possible
  return () => {
    flagSubscribers.delete(subscriber);
    if (subscriber.vendorUnsubscribe) {
      try {
        subscriber.vendorUnsubscribe();
      } catch {
        /* inert */
      }
      subscriber.vendorUnsubscribe = null;
    }
  };
}
